import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/AppLayout';
import ConfirmDialog from '../../components/ConfirmDialog';
import GenerateModal from './GenerateModal';
import useBordereauxFunctions from './useBordereauxFunctions';
import Header from './Header';
import SummaryStats from './SummaryStats';
import Filter from './Filter';
import Table from './Table';
import ValidationRulesPanel from './ValidationRulesPanel';
import BordereauxDetailPanel from './BordereauxDetailPanel';
import { formatDate } from '../regureStyles';

const BordereauxScreen = () => {
  const { t } = useTranslation();
  // D-29: row-click opens the detail panel. Same pattern as invoice
  // detail (D-28); backend already exposes GET /bordereaux/:id.
  const [selectedBordereauxId, setSelectedBordereauxId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // { id, label } or null
  const {
    history,
    bordereaux,
    carriers,
    loading,
    total,
    page,
    setPage,
    PAGE_SIZE,
    filterCarrier,
    setFilterCarrier,
    filterType,
    setFilterType,
    filterStatus,
    setFilterStatus,
    showGenModal,
    setShowGenModal,
    sending,
    fetchBordereaux,
    handleDownload,
    handleSend,
    runDelete,
    selStyle,
    totalReady,
    totalSent,
    totalErr,
  } = useBordereauxFunctions();

  return (
    <AppLayout>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* ── Header ──────────────────────────────────────── */}
        <Header history={history} setShowGenModal={setShowGenModal} carriers={carriers} />

        {/* ── Validation rules (collapsible) ───────────────── */}
        <ValidationRulesPanel />

        {/* ── Summary stats ─────────────────────────────────── */}
        <SummaryStats total={total} totalReady={totalReady} totalSent={totalSent} totalErr={totalErr} loading={loading} />

        {/* ── Filters ───────────────────────────────────────── */}
        <Filter
          filterCarrier={filterCarrier}
          setFilterCarrier={setFilterCarrier}
          setPage={setPage}
          carriers={carriers}
          filterType={filterType}
          setFilterType={setFilterType}
          selStyle={selStyle}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          fetchBordereaux={fetchBordereaux}
        />

        {/* ── Table ─────────────────────────────────────────── */}
        <Table
          loading={loading}
          bordereaux={bordereaux}
          carriers={carriers}
          handleDownload={handleDownload}
          handleSend={handleSend}
          handleDelete={(id) => {
            const row = bordereaux.find(b => b._id === id);
            const label = row
              ? `${row.type || 'bordereau'} (${row.period?.from ? formatDate(row.period.from) : '?'} → ${row.period?.to ? formatDate(row.period.to) : '?'})`
              : 'this bordereau';
            setPendingDelete({ id, label });
          }}
          sending={sending}
          total={total}
          PAGE_SIZE={PAGE_SIZE}
          page={page}
          setPage={setPage}
          onRowClick={setSelectedBordereauxId}
        />
      </div>

      {selectedBordereauxId && (
        <BordereauxDetailPanel
          bordereauxId={selectedBordereauxId}
          onClose={() => setSelectedBordereauxId(null)}
        />
      )}

      {showGenModal && carriers.length > 0 && (
        <GenerateModal
          carriers={carriers}
          onClose={() => setShowGenModal(false)}
          onGenerated={() => {
            setTimeout(fetchBordereaux, 3000);
          }}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          title={t('screens.bordereaux.deleteTitle', { defaultValue: 'Delete bordereau?' })}
          message={t('screens.bordereaux.deleteMessage', {
            defaultValue: `${pendingDelete.label} will be permanently removed. An audit entry will be recorded.`,
            label: pendingDelete.label,
          })}
          confirmText={t('common.delete', { defaultValue: 'Delete' })}
          variant='danger'
          onConfirm={async () => {
            try {
              await runDelete(pendingDelete.id);
            } finally {
              setPendingDelete(null);
            }
          }}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </AppLayout>
  );
};

export default BordereauxScreen;
